import { CloseIcon } from '@danone-global/internal/react/components/icons'
import { Grid, IconButton } from '@danone-global/internal/react/components/ui'
import { Theme } from '@danone-global/internal/react/core'
import { ClassNameMap, makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React, { ReactNode, useEffect, useState } from 'react'
import { CSSTransition } from 'react-transition-group'

export interface Props {
  children: ReactNode
  open: boolean
  onClose?: () => void
  classNames?: ClassNameMap
}

const animationTime = 400

export const Modal: React.FC<Props> = ({
  children,
  open,
  onClose,
  classNames,
}) => {
  const [show, setShow] = useState(false)
  const classes = useStyles({ classes: classNames, show })

  const handleClose = () => {
    if (!onClose) {
      return
    }

    setShow(false)

    setTimeout(() => {
      onClose()
    }, animationTime)
  }

  useEffect(() => {
    setShow(open)
  }, [open])

  return (
    <CSSTransition
      key="modal"
      classNames={{
        enter: classes.enter,
        enterActive: classes.enterActive,
        exit: classes.exit,
        exitActive: classes.exitActive,
      }}
      in={show}
      timeout={animationTime}
      unmountOnExit
    >
      <div className={clsx(classes.root)}>
        <div
          className={classes.overlay}
          data-testid="btn.modal.overlay"
          onClick={handleClose}
        />

        <Grid className={clsx(classes.container)} container>
          {onClose ? (
            <IconButton
              className={clsx('button-close', classes.closeIcon)}
              color="primary-main"
              onClick={handleClose}
              variant="flat"
            >
              <CloseIcon height={20} width={20} />
            </IconButton>
          ) : undefined}
          {children}
        </Grid>
      </div>
    </CSSTransition>
  )
}

export default Modal

interface StyleProps {
  show: boolean
  classes: ClassNameMap
}

export const useStyles = makeStyles<Theme, StyleProps>(
  (theme: Theme) => ({
    // Jest is failing on props
    // '@global': {
    //   body: {
    //     overflow: ({ show }: { show: boolean }) => (show ? 'hidden' : null),
    //   },
    // },

    root: {
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: '100%',
      overflowY: 'scroll',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 900,
    },

    overlay: {
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },

    container: {
      boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.07)',
      borderRadius: theme.shape.borderRadius(),
      backgroundColor: theme.palette.background.default,
      zIndex: 1000,
      padding: theme.spacing(2),
      width: 'auto',
      maxWidth: 'calc(100vw - 10px)',
      maxHeight: '90vh',
      overflow: 'auto',

      [theme.breakpoints.up('sm')]: {
        minWidth: '500px',
        maxWidth: 'calc(100vw - 40px)',
      },
    },

    closeIcon: {
      position: 'absolute',
      top: 10,
      right: 10,
    },

    enter: {
      opacity: 0,
    },

    exit: {
      opacity: 1,
    },

    exitActive: {
      opacity: 0,
      transition: 'opacity 300ms',
    },

    enterActive: {
      opacity: 1,
      transition: 'opacity 300ms',
    },
  }),
  { name: 'Modal' },
)
